import React, { useCallback, useMemo } from "react";

import { isRiskPolicy } from "@design-controls/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  QButton,
  QCheckbox,
  QFormControl,
  QInput,
  QModal,
  QModalBody,
  QModalHeader,
  QRadio,
  QRadioGroup,
  QSpacer,
  QStack,
  QText,
  useToastProvider,
} from "@qualio/ui-components";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { useRiskModal } from "../../hooks/useRiskModal";
import { CreateRiskForm } from "./types";
import { useCreateDesignElement } from "../../hooks/designElement/useCreateDesignElement";
import { useNavigate } from "react-router";

export const RiskModal: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, closeModal } = useRiskModal();
  const configs = useCurrentConfigs();
  const { isLoading, mutate } = useCreateDesignElement();
  const { showToast } = useToastProvider();
  const navigate = useNavigate();

  const methods = useForm<CreateRiskForm>({
    defaultValues: {
      type: "risk",
      subType: "iso",
      title: "",
      addAnother: false,
    },
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(CreateRiskForm),
  });

  const {
    formState: { errors },
  } = methods;

  const showModels = useMemo(() => {
    const riskModel = configs.find(isRiskPolicy);
    return !!riskModel?.assessment?.fmea;
  }, [configs]);

  const onSubmit = useCallback(
    (form: CreateRiskForm) => {
      const { addAnother, ...payload } = form;

      mutate(payload as any, {
        onSuccess: (data) => {
          if (addAnother) {
            methods.reset({
              type: "risk",
              subType: "iso",
              title: "",
              addAnother,
            });
            showToast({
              title: t("common.notifications.createSuccess.title"),
              description: t("common.notifications.createSuccess.description", {
                title: data.title,
              }),
              status: "success",
              duration: 5000,
            });
          } else {
            closeModal();
            navigate(`../risk/${payload.subType}/${data.id}/edit`);
          }
        },
      });
    },
    [mutate, methods, closeModal],
  );

  return (
    <QModal isOpen={isOpen} onClose={closeModal} size="xl">
      <QModalHeader>
        <QText>{t("risks.create.modalTitle")}</QText>
      </QModalHeader>
      <QModalBody>
        <FormProvider {...methods}>
          <form
            noValidate
            onSubmit={methods.handleSubmit(onSubmit)}
            data-cy="create-risk"
          >
            <QStack gap="4">
              <QFormControl
                label={t("risks.create.fields.riskName")}
                isRequired
                isInvalid={!!errors.title}
                error={errors.title?.message}
              >
                <Controller
                  name="title"
                  render={({ field: { onChange, value } }) => (
                    <QInput onChange={onChange} value={value} data-cy="title" />
                  )}
                />
              </QFormControl>

              {showModels && (
                <QFormControl
                  label={t("risks.create.fields.riskModel")}
                  isRequired
                  isInvalid={!!errors.subType}
                  error={errors.subType?.message}
                >
                  <Controller
                    name="subType"
                    render={({ field: { onChange, value } }) => (
                      <QRadioGroup onChange={onChange} value={value}>
                        <QStack columnGap="16px" direction="row" rowGap={0}>
                          <QRadio
                            value="iso"
                            isChecked={value === "iso"}
                            data-cy="iso-risk"
                          >
                            {t("risks.create.riskModels.iso")}
                          </QRadio>
                          <QRadio
                            value="fmea"
                            isChecked={value === "fmea"}
                            data-cy="fmea-risk"
                          >
                            {t("risks.create.riskModels.fmea")}
                          </QRadio>
                        </QStack>
                      </QRadioGroup>
                    )}
                  />
                </QFormControl>
              )}

              <QStack justifyContent="space-between" direction="row">
                <Controller
                  name="addAnother"
                  render={({ field: { value, onChange } }) => (
                    <QCheckbox
                      size="sm"
                      data-cy="add-another-checkbox"
                      onChange={onChange}
                      value={value}
                    >
                      {t("risks.create.actions.addAnother")}
                    </QCheckbox>
                  )}
                />

                <QSpacer />
                <QStack direction="row">
                  <QButton
                    variant="outline"
                    data-cy="cancel-button"
                    onClick={closeModal}
                    isDisabled={isLoading}
                  >
                    {t("risks.create.actions.cancel")}
                  </QButton>
                  <QButton
                    data-cy="submit-button"
                    type="submit"
                    isLoading={isLoading}
                  >
                    {t("risks.create.actions.create")}
                  </QButton>
                </QStack>
              </QStack>
            </QStack>
          </form>
        </FormProvider>
      </QModalBody>
    </QModal>
  );
};
